/*
 * Copyright (c) 2017, Indigen Solutions. All rights reserved.
 *
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 *
 * This software is the confidential and proprietary information.
 *
 * You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement
 * you entered into.
 */

/**
 *
 */
import { ProductHelper } from "./Product.helper";
import * as moment from "moment";
import { Item } from "../types/Item";
import { Customer } from "../types/Customer";
import { Quotation } from "../types/Quotation";
import { User } from "../models/User.model";
import { dicoAA } from "../data/products/aa/dico";
import { ProductAA } from "../types/ProductAA";

export class ProductAAHelper extends ProductHelper {
    public defaultValue: ProductAA = {
        isGPAT: true,
        isGA: true,
        samplingInterval: "month",
        beneficiaries: [{}],
    };

    public async init(advisor: User, customer: Customer, quotation: Quotation) {
        await this.tarifsXLS.read(require("../data/products/aa/form/tarifs.xlsx"));
        await this.propositionPDF.read(require("../data/products/aa/form/proposition.pdf"));
        await this.adhesionPDF.read(require("../data/products/aa/form/adhesion.pdf"));
        await this.readCourrierPDF(advisor);
        super.init(advisor, customer, quotation);
    }

    public getDico() {
        return dicoAA;
    }

    public getGPATAnnuityItems(): Item[] {
        this.tarifsXLS.setActiveSheet("BASE_Tot");
        return this.replaceItemsLabel(this.getItems("B5:K5", true), ",", " ");
    }

    public getGPAPAnnuityItems(): number {
        if (this.product && this.product.GPATAnnuity) {
            return this.product.GPATAnnuity / 2;
        }
        return null;
    }

    public getGCACapitalItems(): Item[] {
        this.tarifsXLS.setActiveSheet("BASE_Cap");
        return this.replaceItemsLabel(this.getItems("B7:D7", true), ",", " ");
    }

    public getGPATContribution(): number {
        const age = this.getAgeAtEffectiveDate();
        if (this.product && this.product.GPATAnnuity) {
            this.tarifsXLS.setActiveSheet("BASE_Tot");
            const result = this.tarifsXLS.findProductInRanges("B5:K5", this.product.GPATAnnuity, "A9:A65", age);
            if (!result) {
                throw new Error("L'age de l'assuré n'est pas pris en charge pour cette prestation");
            } else {
                return result.v;
            }
        }
        return null;
    }

    public getGPAPAnnuityItemsSelect(): Item[] {
        let values = [];
        if (this.product && this.product.GPATAnnuity) {
            values.push({
                name: this.product.GPATAnnuity / 2 + "",
                label: this.product.GPATAnnuity / 2 + "",
            });
        }
        return values;
    }

    public getGPAPContribution(): number {
        const age = this.getAgeAtEffectiveDate();
        if (this.product && this.product.GPAPAnnuity) {
            this.tarifsXLS.setActiveSheet("BASE_Part");
            const result = this.tarifsXLS.findProductInRanges("B6:K6", this.product.GPAPAnnuity, "A9:A65", age);
            return result ? result.v : null;
        }
        return null;
    }

    public getGCAContribution(): number {
        const age = this.getAgeAtEffectiveDate();
        if (this.product && this.product.GCACapital) {
            this.tarifsXLS.setActiveSheet("BASE_Cap");
            const result = this.tarifsXLS.findProductInRanges("B7:D7", this.product.GCACapital, "A9:A65", age);
            return result ? result.v : null;
        }
        return null;
    }

    public getContribution(): number {
        if (this.product) {
            let total = 0;
            if (this.product.GPATContribution && this.product.isGPAT) {
                total += this.product.GPATContribution;
            }
            if (this.product.GPAPContribution && this.product.isGPAP) {
                total += this.product.GPAPContribution;
            }
            if (this.product.GCAContribution && this.product.isGCA) {
                total += this.product.GCAContribution;
            }
            if (this.product.isGA) {
                total += 0.58;
            }
            return total;
        }
        return null;
    }

    public getContributionMax(): number {
        if (this.product) {
            if (this.product.contribution && this.product.isGA) {
                return ((this.product.contribution - 0.58) / 100) * 130 + 0.58;
            }
            if (this.product.contribution && !this.product.isGA) {
                return (this.product.contribution / 100) * 130;
            }
        }
        return null;
    }

    public getPeriodicContribution(): number {
        return this._getPeriodicContribution(this.product.contribution, this.product.samplingInterval);
    }

    public getPeriodicContributionMax(): number {
        return this._getPeriodicContribution(this.product.contributionMax, this.product.samplingInterval);
    }

    public async readCourrierPDF(advisor: User) {
        if (advisor.company.name === "ag2r") {
            await this.courrierAdhesionPDF.read(
                require("../data/products/aa/form/courrier_accompagnement_adhesion.pdf")
            );
            await this.courrierPropositionPDF.read(
                require("../data/products/aa/form/courrier_accompagnement_proposition.pdf")
            );
        } else if (advisor.company.name === "viasante") {
            await this.courrierAdhesionPDF.read(
                require("../data/products/aa/form/courrier_accompagnement_adhesion_viasante.pdf")
            );
            await this.courrierPropositionPDF.read(
                require("../data/products/aa/form/courrier_accompagnement_proposition_viasante.pdf")
            );
        }
    }

    protected getPDFields() {
        return {
            ...this.getAdvisorPDFFields(),
            ...this.getCustomerPDFFields(),

            customer_mrs: this.customer.title === "mrs" || this.customer.title === "miss",

            // Date d'effet souhaitée

            effectiveDate: moment(this.quotation.effectiveDate).format("DDMMYYYY"),
            effectiveDate_month: moment(this.quotation.effectiveDate).format("MM"),
            effectiveDate_year: moment(this.quotation.effectiveDate).format("YY"),
            age: this.getAgeAtDate(this.customer.birthdate, this.quotation.effectiveDate).toString(),

            // Mode de prélèvement

            product_samplingInterval_month: this.product.samplingInterval === "month",
            product_samplingInterval_trimester: this.product.samplingInterval === "trimester",
            product_samplingInterval_month_2: this.product.samplingInterval === "month",
            product_samplingInterval_trimester_2: this.product.samplingInterval === "trimester",

            // Garanties

            product_choice: this.product.contribution,

            product_GPATAnnuity: this.product.isGPAT ? this.product.GPATAnnuity : "",
            product_GPATContribution: this.product.isGPAT ? this.product.GPATContribution.toFixed(2) : "",

            product_GPAPAnnuity: this.product.isGPAP ? this.product.GPAPAnnuity : "",
            product_GPAPContribution: this.product.isGPAP ? this.product.GPAPContribution.toFixed(2) : "",

            product_GCACapital: this.product.isGCA ? this.product.GCACapital : "",
            product_GCAContribution: this.product.isGCA ? this.product.GCAContribution.toFixed(2) : "",

            product_isGA: this.product.isGA ? "Oui" : "Non",
            produtc_GAContribution: this.product.isGA ? "0.58€" : "",

            product_monthlyContribution: this.product.contribution.toFixed(2),

            // Cotisations

            product_periodicContribution: this.getPeriodicContribution(),
            product_periodicContributionMax: this.getPeriodicContributionMax(),
        };
    }

    // Override Date

    public getAgeAtDate(birthdate: string, date: string): number {
        return moment(date)
            .date(1)
            .diff(moment(birthdate).date(1), "year");
    }

    private _getPeriodicContribution(contribution, samplingInterval) {
        switch (samplingInterval) {
            case "month":
                return this.round(contribution);
            case "trimester":
                return this.round(contribution) * 3;
            default:
                return null;
        }
    }
}



// WEBPACK FOOTER //
// ./src/helper/ProductAA.helper.ts